import React, { useRef, useState, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Box,
  Typography,
  Button,
  Avatar,
  Modal,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  TextField,
  OutlinedInput,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MovieEditIcon from "@mui/icons-material/MovieEdit";
import { Snackbar, Alert } from "@mui/material";
import "./VideoUpload.css";
import { useDispatch, useSelector } from "react-redux";
import {
  uploadVideosWithMetadata,
  resetStatus,
  fetchVideos,
} from "../../Redux/VideoUpload";

const CARD_WIDTH = 280;
const GAP = 24;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  bgcolor: "background.paper",
  border: "0.1px solid #000",
  boxShadow: 24,
  borderRadius: "5px",
  overflow: "auto",
  height: "80%",
  width: "90%",
};

const IgniteAdmins = [
  "h.srinivasan",
  "varsha.a.tyagi",
  "uma.rani.battu",
  "ankur.e.mishra",
  "arpita.m.singh",
  "aluru.akash",
  "nikita.apte",
  "srikanth.narabathoju",
  "sindhu.s.nambiar",
  "mohana.krishnaveni",
  "n.sharda",
  "jeevan.shetty",
];

const OtherTalksSection = ({
  talks = [],
  activeTalkId,
  onSelectTalk,
  imageUrls,
}) => {
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success", // success | error
  });
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  let loggedInEnterpriseId = "";
  if (activeAccount)
    loggedInEnterpriseId = activeAccount.idTokenClaims.enterpriseID;
  const isAdmin = IgniteAdmins.includes(loggedInEnterpriseId);

  // Video upload
  const [videos, setVideos] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [formData, setFormData] = useState({
    speakerName: "",
    sessionTitle: "",
    sessionDescription: "",
    sessionDuration: "",
    totalRecordings: "",
    sessionDate: "",
    clipTitle: "",
    clipDescription: "",
    speakerImage: null,
    category: "",
  });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageDragging, setImageDragging] = useState(false);
  const [currentClip, setCurrentClip] = useState({
    file: null,
    duration: null,
    autoThumbnail: null, // from video
    customThumbnail: null, // uploaded
    customThumbnailFile: null,
    clipTitle: "",
    clipDescription: "",
  });
  const latestVideo = videos[videos.length - 1];

  const handleUpload = (e) => {
    const files = Array.from(e.target.files);
    e.target.value = null;

    const validVideos = files.filter((file) => file.type.startsWith("video/"));

    if (!validVideos.length) return;

    const file = validVideos[0]; // only one preview at a time

    const url = URL.createObjectURL(file);
    const videoElement = document.createElement("video");

    videoElement.preload = "metadata";
    videoElement.src = url;

    videoElement.onloadedmetadata = () => {
      const duration = videoElement.duration;

      // ✅ overwrite state (IMPORTANT)
      setCurrentClip((prev) => ({
        ...prev,
        file,
        duration,
        autoThumbnail: null,
      }));

      videoElement.currentTime = 0.1;

      videoElement.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(videoElement, 0, 0);

          const thumbnail = canvas.toDataURL("image/png");

          setCurrentClip((prev) => {
            if (prev.customThumbnail) return prev;

            return {
              ...prev,
              autoThumbnail: thumbnail,
            };
          });
        } catch (err) {
          console.log("Thumbnail failed");
        }

        URL.revokeObjectURL(url);
      };
    };
  };

  const handleRemove = (index) => {
    const updated = [...videos];
    updated.splice(index, 1);
    setVideos(updated);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    const files = Array.from(e.dataTransfer.files);
    const validVideos = files.filter((f) => f.type.startsWith("video/"));

    if (!validVideos.length) return;

    const file = validVideos[0];

    const url = URL.createObjectURL(file);
    const videoElement = document.createElement("video");

    videoElement.preload = "metadata";
    videoElement.src = url;

    videoElement.onloadedmetadata = () => {
      const duration = videoElement.duration;

      // ✅ overwrite state
      setCurrentClip((prev) => ({
        ...prev,
        file,
        duration,
        autoThumbnail: null,
      }));

      videoElement.currentTime = 0.1;

      videoElement.onseeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = videoElement.videoWidth;
          canvas.height = videoElement.videoHeight;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(videoElement, 0, 0);

          const thumbnail = canvas.toDataURL("image/png");

          setCurrentClip((prev) => {
            if (prev.customThumbnail) return prev;

            return {
              ...prev,
              autoThumbnail: thumbnail,
            };
          });
        } catch (err) {
          console.log("Thumbnail failed");
        }

        URL.revokeObjectURL(url);
      };
    };
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const formatDuration = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes}:${secs.toString().padStart(2, "0")}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = (file) => {
    if (!file || !file.type.startsWith("image/")) return;

    const previewUrl = URL.createObjectURL(file);

    setFormData((prev) => ({
      ...prev,
      speakerImage: file,
    }));

    setImagePreview(previewUrl);
  };

  const dispatch = useDispatch();

  const handleSubmit = () => {
    const formattedVideos = videos.map((v) => ({
      clipTitle: v.clipTitle,
      clipDescription: v.clipDescription,
      duration: v.duration,
      fileName: v.file ? v.file.name : "",
      file: v.file,
      videoUrl: v.videoUrl,
      thumbnailUrl: v.thumbnail,
      thumbnailFile: v.thumbnailFile,
    }));

    if (!formData.speakerName) {
      alert("Speaker name is required");
      return;
    }

    if (!videos.length) {
      alert("Please add at least one video");
      return;
    }

    dispatch(
      uploadVideosWithMetadata({
        formData,
        videos: formattedVideos,
      })
    );
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setImageDragging(false);

    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
  };

  const handleImageDragEnter = () => setImageDragging(true);
  const handleImageDragLeave = () => setImageDragging(false);

  useEffect(() => {
    dispatch(fetchVideos());
  }, [dispatch]);

  const { loader, error, success } = useSelector((state) => state.VideoUpload);

  // Dynamic filter state
  const [selectedCategory, setSelectedCategory] = useState("ALL");

  // Dynamic categories from talks
  const categories = ["ALL", ...new Set(talks.map((t) => t.category))];

  // const otherTalks = talks.filter((t) =>
  //   selectedCategory === "ALL" ? true : t.category === selectedCategory
  // );

  const filteredTalks = talks.filter((t) =>
    selectedCategory === "ALL" ? true : t.category === selectedCategory
  );

  const otherTalks =
    selectedCategory === "ALL"
      ? filteredTalks.sort((a, b) => {
          const dateA = a.courseDate ? new Date(a.courseDate).getTime() : 0;
          const dateB = b.courseDate ? new Date(b.courseDate).getTime() : 0;
          return dateB - dateA;
        })
      : filteredTalks;

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = new Date(dateString);

    const day = String(date.getDate()).padStart(2, "0");

    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();

    return `${day}-${month}-${year}`;
  };

  const [activePage, setActivePage] = useState(0);

  useEffect(() => {
    if (success) {
      setSnackbar({
        open: true,
        message: success,
        severity: "success",
      });

      setTimeout(() => {
        setFormData({
          speakerName: "",
          sessionTitle: "",
          sessionDescription: "",
          sessionDuration: "",
          totalRecordings: "",
          sessionDate: "",
          clipTitle: "",
          clipDescription: "",
          speakerImage: null,
          category: "",
        });

        setVideos([]);
        setImagePreview(null);
        setOpen(false);

        dispatch(fetchVideos());
        dispatch(resetStatus());
      }, 800);
    }

    if (error) {
      setSnackbar({
        open: true,
        message: error,
        severity: "error",
      });

      dispatch(resetStatus());
    }
  }, [success, error, dispatch]);

  const ITEMS_PER_PAGE = 4;
  const pageCount = Math.ceil(otherTalks.length / ITEMS_PER_PAGE);

  const paginatedTalks = otherTalks.slice(
    activePage * ITEMS_PER_PAGE,
    (activePage + 1) * ITEMS_PER_PAGE
  );

  const previewThumbnail =
    currentClip.customThumbnail || currentClip.autoThumbnail;

  const [speakerImageDragging, setSpeakerImageDragging] = useState(false);
  const [thumbnailDragging, setThumbnailDragging] = useState(false);

  const handleSpeakerImageDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSpeakerImageDragging(false);

    const file = e.dataTransfer.files[0];
    handleImageUpload(file);
  };

  const handleThumbnailDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setThumbnailDragging(false);

    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith("image/")) return;

    const previewUrl = URL.createObjectURL(file);

    setCurrentClip((prev) => ({
      ...prev,
      customThumbnail: previewUrl,
      customThumbnailFile: file,
    }));
  };

  const handleSpeakerDragEnter = () => setSpeakerImageDragging(true);
  const handleSpeakerDragLeave = () => setSpeakerImageDragging(false);

  const handleThumbnailDragEnter = () => setThumbnailDragging(true);
  const handleThumbnailDragLeave = () => setThumbnailDragging(false);

  const handleCloseModal = () => {
    setFormData({
      speakerName: "",
      sessionTitle: "",
      sessionDescription: "",
      sessionDuration: "",
      totalRecordings: "",
      sessionDate: "",
      clipTitle: "",
      clipDescription: "",
      speakerImage: null,
      category: "",
    });

    setVideos([]);
    setImagePreview(null);

    setCurrentClip({
      file: null,
      duration: null,
      autoThumbnail: null,
      customThumbnail: null,
      clipTitle: "",
      clipDescription: "",
    });

    setOpen(false);
  };

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 2,
        }}
        className="other-talks-row-box"
      >
        <Typography className="other-talks-title">Other Talks</Typography>

        <Box sx={{ display: "flex", gap: 2 }} className="other-talks-cat-box">
          {categories.map((cat) => (
            <Button
              key={cat}
              onClick={() => {
                setSelectedCategory(cat);
                setActivePage(0);
              }}
              variant="contained"
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: 0,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                opacity: selectedCategory === cat ? 1 : 0.6,
                "&:hover": { backgroundColor: "#000" },
              }}
            >
              {cat}
            </Button>
          ))}
          {isAdmin && (
            <Button
              onClick={() => setOpen(true)}
              sx={{
                backgroundColor: "#000",
                color: "#fff",
                borderRadius: 0,
                px: 3,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                opacity: 1,
                "&:hover": { backgroundColor: "#000" },
              }}
            >
              <img
                style={{ width: "20%", paddingRight: "10px" }}
                src={
                  process.env.PUBLIC_URL + "/Assets/IgniteTalks/upload_icon.png"
                }
                className="upload_icon"
              />
              Upload videos
            </Button>
          )}
        </Box>
      </Box>

      <Modal
        open={open}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style} className="modal-root">
          <Box className="modal-header">
            <Typography className="close-btn" onClick={handleCloseModal}>
              Close ✕
            </Typography>
          </Box>

          <Box className="modal-body">
            {/* LEFT SECTION */}
            <Box className="left-section">
              <Typography className="section-title" variant="h1">
                Upload Ignite Talks
              </Typography>

              <Box className="left-section-wrap">
                <Box className="form-row">
                  <Box sx={{ width: "100%" }}>
                    <label
                      htmlFor="speaker-name"
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Speaker name
                    </label>
                    <OutlinedInput
                      id="speaker-name"
                      placeholder="Type here..."
                      fullWidth
                      name="speakerName"
                      value={formData?.speakerName}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "0",
                        height: "2rem",
                      }}
                    />
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <label
                      htmlFor="session-title"
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Session Title
                    </label>
                    <OutlinedInput
                      id="session-title"
                      placeholder="Type here..."
                      fullWidth
                      name="sessionTitle"
                      value={formData?.sessionTitle}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "0",
                        height: "2rem",
                      }}
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <label
                        htmlFor="speaker-description"
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Session description
                      </label>
                      <Typography
                        variant="small"
                        sx={{
                          fontSize: "12px",
                          textAlign: "end",
                          minWidth: "fit-content",
                        }}
                      >
                        Max 300 characters
                      </Typography>
                    </Box>
                    <OutlinedInput
                      id="speaker-description"
                      placeholder="Type here..."
                      fullWidth
                      name="sessionDescription"
                      value={formData.sessionDescription}
                      onChange={handleChange}
                      sx={{
                        borderRadius: "0",
                        height: "4rem",
                      }}
                    />
                  </Box>
                  <Box>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Category
                    </label>

                    <Select
                      fullWidth
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ height: "2rem", borderRadius: 0 }}
                    >
                      <MenuItem value="" disabled>
                        Select category
                      </MenuItem>
                      <MenuItem value="Digital Customer">
                        Digital Customer
                      </MenuItem>
                      <MenuItem value="Technology">Technology</MenuItem>
                      <MenuItem value="Design">Design</MenuItem>
                      <MenuItem value="Gen AI">Gen AI</MenuItem>
                      <MenuItem value="Success story">Success story</MenuItem>
                      <MenuItem value="Best practices">Best practices</MenuItem>
                      <MenuItem value="LCNC">LCNC</MenuItem>
                    </Select>
                  </Box>
                </Box>
                <Box>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Upload speaker’s profile image
                  </label>

                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #ccc",
                      height: "8.1rem",
                      cursor: "pointer",
                    }}
                  >
                    {/* LEFT SIDE - UPLOAD */}
                    <Box
                      onDragOver={handleImageDragOver}
                      onDrop={handleSpeakerImageDrop}
                      onDragEnter={handleSpeakerDragEnter}
                      onDragLeave={handleSpeakerDragLeave}
                      sx={{
                        backgroundColor: speakerImageDragging
                          ? "#f5f5f5"
                          : "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        Drag and drop to upload
                      </Typography>

                      <CloudUploadIcon sx={{ fontSize: 30 }} />

                      <Button
                        variant="contained"
                        component="label"
                        sx={{
                          backgroundColor: "#000",
                          borderRadius: 0,
                          fontSize: "12px",
                          "&:hover": { backgroundColor: "#000" },
                        }}
                      >
                        Choose File
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => handleImageUpload(e.target.files[0])}
                        />
                      </Button>
                    </Box>

                    {/* RIGHT SIDE - PREVIEW */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      {imagePreview ? (
                        <Avatar
                          src={imagePreview}
                          sx={{ width: "100%", height: "100%" }}
                        />
                      ) : (
                        <>
                          <Avatar sx={{ width: 50, height: 50 }} />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#666",
                              textAlign: "center",
                            }}
                          >
                            No files have been uploaded
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box className="form-row-bottom">
                <Box>
                  <label
                    htmlFor="session-duration"
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Session Duration (Minutes)
                  </label>
                  <OutlinedInput
                    id="session-duration"
                    placeholder="Type here..."
                    fullWidth
                    name="sessionDuration"
                    value={formData.sessionDuration}
                    onChange={handleChange}
                    sx={{
                      borderRadius: "0",
                      height: "2rem",
                    }}
                  />
                </Box>
                <Box>
                  <label
                    htmlFor="total-recordings"
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Total Recordings
                  </label>
                  <OutlinedInput
                    id="total-recordings"
                    placeholder="Type here..."
                    fullWidth
                    name="totalRecordings"
                    value={formData.totalRecordings}
                    onChange={handleChange}
                    sx={{
                      borderRadius: "0",
                      height: "2rem",
                    }}
                  />
                </Box>
                <Box>
                  <label
                    htmlFor="session-date"
                    style={{
                      display: "block",
                      marginBottom: "5px",
                      fontSize: "13px",
                      fontWeight: 700,
                    }}
                  >
                    Session Date
                  </label>
                  <TextField
                    type="date"
                    name="sessionDate"
                    size="small"
                    value={formData.sessionDate}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>
              </Box>

              <hr className="divider" />

              <Typography className="section-subtitle">
                Upload session clips
              </Typography>

              <Box className="left-section-wrap">
                <Box className="form-row">
                  <Box sx={{ width: "100%" }}>
                    <label
                      htmlFor="clipTitle"
                      style={{
                        display: "block",
                        marginBottom: "5px",
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Clip Title
                    </label>
                    <OutlinedInput
                      id="clipTitle"
                      placeholder="Enter here..."
                      fullWidth
                      name="clipTitle"
                      value={currentClip.clipTitle}
                      onChange={(e) =>
                        setCurrentClip((prev) => ({
                          ...prev,
                          clipTitle: e.target.value,
                        }))
                      }
                      sx={{
                        borderRadius: "0",
                        height: "2rem",
                      }}
                    />
                  </Box>
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <label
                        htmlFor="speaker-description"
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          fontSize: "13px",
                          fontWeight: 700,
                        }}
                      >
                        Clip description
                      </label>
                      <Typography
                        variant="small"
                        sx={{
                          fontSize: "12px",
                          textAlign: "end",
                          minWidth: "fit-content",
                        }}
                      >
                        Max 100 characters
                      </Typography>
                    </Box>
                    <OutlinedInput
                      id="clipDescription"
                      placeholder="Enter here..."
                      fullWidth
                      name="clipDescription"
                      value={currentClip.clipDescription}
                      onChange={(e) =>
                        setCurrentClip((prev) => ({
                          ...prev,
                          clipDescription: e.target.value,
                        }))
                      }
                      sx={{
                        borderRadius: "0",
                        height: "4rem",
                      }}
                    />
                  </Box>
                </Box>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: "5px",
                    }}
                  >
                    <label
                      style={{
                        fontSize: "13px",
                        fontWeight: 700,
                      }}
                    >
                      Upload video
                    </label>

                    <Typography sx={{ fontSize: "12px" }}>
                      Max 10 Uploads
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      border: "1px solid #ccc",
                      height: "8.1rem",
                      cursor: "pointer",
                    }}
                  >
                    {/* LEFT SIDE - UPLOAD */}
                    <Box
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                      sx={{
                        backgroundColor: dragging ? "#f5f5f5" : "#fafafa",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        gap: "5px",
                        borderRight: "1px solid #ccc",
                      }}
                    >
                      <Typography
                        sx={{ fontSize: "12px", textAlign: "center" }}
                      >
                        Drag and drop to upload
                      </Typography>

                      <CloudUploadIcon sx={{ fontSize: 30 }} />

                      <Button
                        variant="contained"
                        component="label"
                        sx={{
                          backgroundColor: "#000",
                          borderRadius: 0,
                          fontSize: "12px",
                          "&:hover": { backgroundColor: "#000" },
                        }}
                      >
                        Choose File
                        <input
                          id="video-upload"
                          type="file"
                          multiple
                          accept="video/*"
                          hidden
                          onChange={handleUpload}
                        />
                      </Button>
                    </Box>

                    {/* RIGHT SIDE - PREVIEW */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "5px",
                      }}
                    >
                      {currentClip.file ? (
                        <>
                          <img
                            src={previewThumbnail || "/Assets/placeholder.png"}
                            alt="preview"
                            style={{
                              height: "50%",
                              objectFit: "cover",
                            }}
                          />
                          <Typography
                            sx={{ fontSize: "11px", textAlign: "center" }}
                          >
                            {currentClip?.file?.name}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <MovieEditIcon
                            sx={{ height: 50, width: 50, color: "#ccc" }}
                          />
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#666",
                              textAlign: "center",
                            }}
                          >
                            No files have been uploaded
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ width: "47%" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "5px",
                    fontSize: "13px",
                    fontWeight: 700,
                  }}
                >
                  Upload clip thumbnail
                </label>

                <Box
                  sx={{
                    display: "flex",
                    border: "1px solid #ccc",
                    height: "8.1rem",
                    cursor: "pointer",
                  }}
                >
                  {/* LEFT SIDE - UPLOAD */}
                  <Box
                    onDragOver={handleImageDragOver}
                    onDrop={handleThumbnailDrop}
                    onDragEnter={handleThumbnailDragEnter}
                    onDragLeave={handleThumbnailDragLeave}
                    sx={{
                      backgroundColor: thumbnailDragging
                        ? "#f5f5f5"
                        : "#fafafa",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "5px",
                      borderRight: "1px solid #ccc",
                      width: "11rem",
                    }}
                  >
                    <Typography sx={{ fontSize: "12px", textAlign: "center" }}>
                      Drag and drop to upload
                    </Typography>

                    <CloudUploadIcon sx={{ fontSize: 30 }} />

                    <Button
                      variant="contained"
                      component="label"
                      sx={{
                        backgroundColor: "#000",
                        borderRadius: 0,
                        fontSize: "12px",
                        "&:hover": { backgroundColor: "#000" },
                      }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (!file) return;

                          const previewUrl = URL.createObjectURL(file);

                          setCurrentClip((prev) => ({
                            ...prev,
                            customThumbnail: previewUrl,
                            customThumbnailFile: file,
                          }));
                        }}
                      />
                    </Button>
                  </Box>

                  {/* RIGHT SIDE - PREVIEW */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      flexDirection: "column",
                      gap: "5px",
                      padding: "2rem",
                      width: "12rem",
                    }}
                  >
                    {previewThumbnail ? (
                      <>
                        <img
                          src={previewThumbnail || "/Assets/placeholder.png"}
                          alt="preview"
                          style={{
                            width: "100%",
                            objectFit: "cover",
                          }}
                        />
                      </>
                    ) : (
                      <>
                        <MovieEditIcon
                          sx={{ height: 50, width: 50, color: "#ccc" }}
                        />
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#666",
                            textAlign: "center",
                          }}
                        >
                          No files have been uploaded
                        </Typography>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>

              <Box className="footer-btns">
                <Button
                  className="cancel-btn"
                  sx={{ color: "#a100ff" }}
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  className="upload-btn"
                  sx={{ width: "auto", backgroundColor: "#a100ff" }}
                  onClick={() => {
                    if (!currentClip.file) {
                      alert("Please upload a video");
                      return;
                    }

                    if (!currentClip.clipTitle) {
                      alert("Clip title required");
                      return;
                    }

                    setVideos((prev) => [
                      ...prev,
                      {
                        file: currentClip.file,
                        duration: currentClip.duration,
                        clipTitle: currentClip.clipTitle,
                        clipDescription: currentClip.clipDescription,
                        thumbnail:
                          currentClip.customThumbnail ||
                          currentClip.autoThumbnail,
                        thumbnailFile: currentClip.customThumbnailFile || null,
                        isNew: true,
                      },
                    ]);

                    setCurrentClip({
                      file: null,
                      duration: null,
                      autoThumbnail: null,
                      customThumbnail: null,
                      customThumbnailFile: null,
                      clipTitle: "",
                      clipDescription: "",
                    });

                    const input = document.getElementById("video-upload");
                    if (input) input.value = null;
                  }}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  className="upload-btn"
                  sx={{ width: "auto", backgroundColor: "#a100ff" }}
                  onClick={handleSubmit}
                  disabled={loader}
                >
                  {loader ? <CircularProgress size={20} /> : "Upload"}
                </Button>
              </Box>
            </Box>

            {/* RIGHT SECTION */}
            <Box className="right-section">
              <Box className="right-header">
                <Typography>Uploaded video files</Typography>
                <Typography>Total uploads {videos?.length}</Typography>
              </Box>

              {videos.map((video, index) => (
                <Box key={index} className="video-card">
                  <img
                    src={video?.thumbnail || "/Assets/placeholder.png"}
                    alt="thumbnail"
                    className="thumb"
                  />

                  <Box className="video-info">
                    <Typography className="video-title">
                      <b>{video?.clipTitle || video?.file?.name}</b>
                    </Typography>

                    <Typography className="video-title">
                      {video?.clipDescription}
                    </Typography>

                    <Box className="video-meta">
                      <AccessTimeIcon />
                      {formatDuration(video?.duration)} mins
                    </Box>
                  </Box>

                  <IconButton onClick={() => handleRemove(index)}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>

      {otherTalks.length === 0 && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            py: 8,
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "#555" }}>
            No Ignite Talks available yet.
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: `${GAP}px`,
          pb: 3,
        }}
      >
        {paginatedTalks.map((talk, index) => {
          // const latestDate = Math.max(
          //   ...otherTalks.map((t) => new Date(t.courseDate))
          // );

          // const isLatest = new Date(talk.courseDate).getTime() === latestDate;
          const rawThumb = talk.episodes?.[0]?.thumbnail;

          const thumb = !rawThumb
            ? "/Assets/placeholder.png"
            : rawThumb.startsWith("http") || rawThumb.startsWith("data:")
            ? rawThumb
            : imageUrls?.[rawThumb] || "/Assets/placeholder.png";

          const speakerImg = !talk.speakerImage
            ? null
            : talk.speakerImage.startsWith("http") ||
              talk.speakerImage.startsWith("data:")
            ? talk.speakerImage
            : imageUrls?.[talk.speakerImage];

          const latestTalkId = talks.length
            ? talks.reduce((latest, talk) => {
                if (!latest) return talk;

                const latestDate = latest.courseDate
                  ? new Date(latest.courseDate).getTime()
                  : 0;

                const currentDate = talk.courseDate
                  ? new Date(talk.courseDate).getTime()
                  : 0;

                return currentDate > latestDate ? talk : latest;
              }, null)?.id
            : null;

          return (
            <Box
              key={talk.id}
              onClick={() => onSelectTalk(talk)}
              sx={{
                cursor: "pointer",
                backgroundColor: "#fff",
                border: "1px solid #e0e0e0",
                boxShadow:
                  "0 4px 8px rgba(0,0,0,0.12), 0 6px 10px rgba(0,0,0,0.14)",
              }}
            >
              <Box sx={{ position: "relative" }}>
                {talk.id === latestTalkId && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 0,
                      backgroundColor: "#224BFF",
                      color: "#fff",
                      fontSize: "12px",
                      fontWeight: 700,
                      px: 1,
                      py: "2px",
                      zIndex: 9999,
                    }}
                  >
                    NEW
                  </Box>
                )}
                <Box
                  component="img"
                  src={thumb}
                  alt={talk.title}
                  sx={{ width: "100%", height: 170, objectFit: "cover" }}
                  style={{ filter: "brightness(0.5)" }}
                />

                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      backgroundColor: "#A100FF",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: "8px solid transparent",
                        borderBottom: "8px solid transparent",
                        borderLeft: "12px solid #fff",
                        ml: "3px",
                      }}
                    />
                  </Box>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                <Typography
                  sx={{
                    fontSize: 15,
                    fontWeight: 700,
                    color: "#A100FF",
                    lineHeight: 1.4,
                    mb: 0.5,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {talk?.title}
                </Typography>

                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, my: 1 }}
                >
                  <Typography sx={{ fontSize: 12 }}>Speaker:</Typography>
                  <Avatar
                    src={speakerImg || "/Assets/avatar.png"}
                    sx={{ width: 18, height: 18 }}
                  />
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    {talk?.speakerName
                      ?.toLowerCase()
                      .split(" ")
                      .map(
                        (word) => word.charAt(0).toUpperCase() + word.slice(1)
                      )
                      .join(" ")}
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: 13,
                    color: "#666",
                    lineHeight: 1.5,
                    mb: 2,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {talk?.courseDescription}
                </Typography>

                <Box
                  sx={{
                    mt: "auto",
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: 11,
                    color: "#666",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <AccessTimeIcon sx={{ fontSize: 16 }} />
                    {talk?.duration} mins
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <EventIcon sx={{ fontSize: 16 }} />
                    {formatDate(talk?.courseDate)}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {pageCount > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: 1,
            mt: 2,
            mb: 2,
          }}
          className="scrollable-option"
        >
          {Array.from({ length: pageCount }).map((_, index) => (
            <Box
              key={index}
              onClick={() => setActivePage(index)}
              sx={{
                width: activePage === index ? 22 : 8,
                height: 8,
                borderRadius: 0,
                backgroundColor: activePage === index ? "#000" : "#bbb",
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default OtherTalksSection;
